import React from "react";
import { IPagination } from "../screenshots/screenshots.component";
import { ShowErrorFromResponse } from "../../utilities/response-processor";
import { getPricingClient } from "../../services/api-clients.service";
import SubHeader from "../../layout/components/sub-header/sub-header.component";
import ContentWrapper from "../../layout/components/content-wrapper/content-wrapper.component";
import { Card } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { DateTimeDisplayUTC } from "../../components/date-time-display/date-time-display-utc.component";
import { UserPriceCheckLogResponseModel } from "../../swagger-clients/s365-admin-panel-clients.service";
import { UserSlimDisplay } from "../../components/user-slim-display/user-slim-display";

type PricingInquiriesPageProps = {}

export const PricingInquiriesPage: React.FC<PricingInquiriesPageProps> = () => {

    const [items, setItems] = React.useState<UserPriceCheckLogResponseModel[]>([]);
    const [pagination, setPagination] = React.useState<IPagination>({
        currentPage: 1,
        recordsPerPage: 20,
        totalRecords: 0
    });
    const [searchTerm, setSearchTerm] = React.useState<string>("");
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        getPriceCheckLogs();
    }, [pagination.currentPage, pagination.recordsPerPage]);

    const getPriceCheckLogs = async () => {
        try {
            setItems([]);
            setIsLoading(true);

            const { recordsPerPage, currentPage } = pagination;
            const client = getPricingClient();
            const response = await client.getPriceCheckLogs(searchTerm || null, (currentPage - 1) * recordsPerPage, recordsPerPage);

            setItems(response?.items ?? []);
            setPagination(s => ({ ...s, totalRecords: response?.totalItems ?? 0 }));
        } catch (error) {
            ShowErrorFromResponse(error, "An error occurred while getting pricing inquiries.");
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = () => {
        if (pagination.currentPage !== 1) {
            setPagination(s => ({ ...s, currentPage: 1 }));
        } else {
            getPriceCheckLogs();
        }
    };

    const onClear = () => {
        setSearchTerm("");
    };

    const { totalRecords, currentPage, recordsPerPage } = pagination;
    const totalPages = Math.ceil(totalRecords / recordsPerPage);

    return (
        <div>
            <SubHeader key="sub-header" title="Pricing inquiries" leftItems={[]} rightItems={[]} />
            <ContentWrapper key="content-wrapper" className="pl-4 pr-4">
                <Card className="mb-4">
                    <div className="pt-3 pl-3 pr-3" style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", alignItems: "center" }}>
                        <div className="input-group mr-2 mb-3" style={{ width: "350px" }}>
                            <span className="input-group-text"><i className="far fa-user"></i></span>
                            <input
                                type="text"
                                name="searchTerm"
                                className="form-control"
                                onChange={ev => setSearchTerm(ev.target.value)}
                                onKeyDown={ev => { if (ev.key === "Enter") onSubmit(); }}
                                placeholder="Display name or User principal name or Email"
                                value={searchTerm}
                            />
                        </div>
                        <div className="mb-3">
                            <button type="button" className="btn btn-primary mr-2" onClick={onSubmit}>Search</button>
                            <button type="button" className="btn btn-default" onClick={onClear}>
                                <i className="fas fa-eraser" />
                            </button>
                        </div>
                        <div className="mb-3" style={{ marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                            <b style={{ fontSize: "1.5em", lineHeight: "1em" }}>{totalRecords}</b>
                            <span style={{ fontSize: "0.8em", lineHeight: "0.8em" }}>Total records</span>
                        </div>
                        <div className="ml-3 mb-3">
                            <select
                                className="form-control form-select"
                                value={recordsPerPage}
                                onChange={event => setPagination(s => ({ ...s, recordsPerPage: +event.target.value, currentPage: 1 }))}>
                                <option value="20">20</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                    </div>
                </Card>
                <Card>
                    <Card.Body>
                        <div>
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th className="text-center">Hours</th>
                                        <th className="text-center">Engineers</th>
                                        <th className="text-center">Months</th>
                                        <th className="text-center">Created At</th>
                                        <th className="text-center">Your Price</th>
                                        <th className="text-center">Price/Hour</th>
                                        <th className="text-center">Discount</th>
                                        <th className="text-center">Price/Month</th>
                                        <th className="text-center">Price/Eng/Month</th>
                                        <th className="text-center">Price/Day</th>
                                        <th className="text-center">Price/Eng/Day</th>
                                        <th className="text-center">Hours/Month</th>
                                        <th className="text-center">Hours/Eng/Month</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map(item => (
                                        <tr key={item.id}>
                                            <td className="align-middle">
                                                <UserSlimDisplay user={item.user} showDetailsOnClick />
                                            </td>
                                            <td className="align-middle text-center">{item.hours}</td>
                                            <td className="align-middle text-center">{item.engineers}</td>
                                            <td className="align-middle text-center">{item.months}</td>
                                            <td className="align-middle text-center">
                                                <DateTimeDisplayUTC date={new Date(item.calledAtUtc)} />
                                            </td>
                                            <td className="align-middle text-center">€{item.yourPrice.toFixed(2)}</td>
                                            <td className="align-middle text-center">€{item.pricePerHour.toFixed(2)}</td>
                                            <td className="align-middle text-center">{(item.yourDiscount * 100).toFixed(2)}%</td>
                                            <td className="align-middle text-center">€{item.pricePerMonth.toFixed(2)}</td>
                                            <td className="align-middle text-center">€{item.pricePerEngineerMonth.toFixed(2)}</td>
                                            <td className="align-middle text-center">€{item.pricePerDay.toFixed(2)}</td>
                                            <td className="align-middle text-center">€{item.pricePerEngineerDay.toFixed(2)}</td>
                                            <td className="align-middle text-center">{item.hoursPerMonth.toFixed(2)}</td>
                                            <td className="align-middle text-center">{item.hoursPerEngineerMonth.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                    {isLoading && (
                                        <tr>
                                            <td colSpan={14} className="text-center">Loading...</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>

                            {totalPages > 1 && (
                                <div className="d-flex align-items-center">
                                    <ReactPaginate
                                        previousLabel={"previous"}
                                        nextLabel={"next"}
                                        breakLabel={"..."}
                                        breakClassName={"break-me page-item"}
                                        breakLinkClassName={"page-link"}
                                        pageClassName={"page-item"}
                                        pageLinkClassName={"page-link"}
                                        pageCount={totalPages}
                                        marginPagesDisplayed={4}
                                        pageRangeDisplayed={10}
                                        previousClassName={"page-item"}
                                        previousLinkClassName={"page-link"}
                                        nextClassName={"page-item"}
                                        nextLinkClassName={"page-link"}
                                        forcePage={currentPage - 1}
                                        onPageChange={(page: any) => {
                                            setPagination(s => ({ ...s, currentPage: page.selected + 1 }));
                                            document.body.scrollTop = 0;
                                            document.documentElement.scrollTop = 0;
                                        }}
                                        containerClassName={"pagination mb-0"}
                                        activeClassName={"active"}
                                    />
                                </div>
                            )}
                        </div>
                    </Card.Body>
                </Card>
            </ContentWrapper>
        </div>
    );
};
